import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import useFieldClass from "../../../functions/useFieldClass"; // Import the useFieldClass hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import {
  selectDropPipeComments,
  selectGeneralStation,
  selectDropPipes,
  selectFieldDescriptions,
} from "../../../redux/selectors";
import { fetchJobDetails } from "../../../redux/slices/fetchJobDetailsSlice"; // Adjust the path as necessary
import { clearSectionJobDetails } from "../../../redux/slices/jobDetailsSlice";
import { postData, deleteData } from "../../../redux/slices/apiCallSlice"; // Adjust the path as necessary
import SaveBanner from "./SaveBanner";
import { postChangeLog } from "../../../redux/slices/postChangeLogSlice"; // Import postChangeLog

const DropPipeSpecs = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "dropPipes";

  const jobId = useSelector((state) => state.jobId.jobId);
  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectDropPipeComments);
  const dropPipeDetails = useSelector(selectDropPipes);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const generalSpecsDetails = useSelector(selectGeneralStation);
  const stationColor =
    generalSpecsDetails.find((detail) => detail.Zone === activetab)?.Color ||
    "";

  const loading = storedJobDetails.length === 0;

  const { formData, setFormData } = useFormState({});

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { handleFocus, handleBlur } = useHandleBlur(formData);

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  /****************Add dropdown options here*************************/
  const paintColorOptions =
    options?.filter((option) => option.DropdownType === "Pipe Color")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  /******************************************************************/

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  useEffect(() => {
    const filteredPipes = dropPipeDetails.filter(
      (pipe) => pipe.Zone === activetab && pipe.ProjectID === jobDetails.ProjectID
    );

    const initialFormData = filteredPipes.reduce((acc, pipe) => {
      acc[pipe.ID] = pipe;
      return acc;
    }, {});

    setFormData(initialFormData);
  }, [dropPipeDetails, activetab, jobDetails, setFormData]);

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
  });

  const [, setError] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  useEffect(() => {
    setRoles((prevRoles) => ({ ...prevRoles, ...mapUserRoles(userRoles) }));
  }, [userRoles]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const isDisabled = isJobCheckedOut && !isCurrentUserCheckedOut;

  const handleChange = (e, pipeId) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [pipeId]: {
        ...prevData[pipeId],
        [name]: value,
      },
    }));
  };

  const fieldClasses = useFieldClass(formData, activetab); // Use useFieldClass hook

  // Helper function to determine the highlight class for a field
  const getFieldHighlightClass = (fieldName, pipeId) => {
    if (fieldClasses?.[pipeId]?.[fieldName]) {
      return fieldClasses[pipeId][fieldName]; // Use fieldClasses for dynamic highlighting
    }
    return ""; // Default class
  };

  const handleAddDropPipe = async () => {
    const newDropPipe = {
      projectID: jobId,
      zone: activetab,
      saleQuoteID: jobDetails.SaleQuoteID,
      dropPipeCommentsID: jobDetails.ID,
      dropPipeID: String(dropPipeDetails.length + 1),
    };

    // Validate newDropPipe object
    if (!newDropPipe.projectID || !newDropPipe.zone || !newDropPipe.dropPipeCommentsID) {
      setError("All required fields must be filled out before adding a drop pipe.");
      return;
    }
  
    try {
      // Post the new drop pipe and refetch job details
      await dispatch(postData({ url: apiUrl, data: newDropPipe })).unwrap();
  
      // Post to change log after successful addition
      const pipeDescription = `Drop Pipe ${newDropPipe.dropPipeID}`;
      await dispatch(postChangeLog({
        projectID: jobId,
        currentUser: currentUser,
        userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
        section: "Drop Pipe Specs",
        field: pipeDescription,
        prevValue: "",
        newValue: "ADDED",
        dateTime: new Date().toISOString()
      })).unwrap();
  
      const updatedDetails = await dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })).unwrap();

  
      setFormData((prevData) => {
        const updatedFormData = { ...prevData };
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((pipe) => {
            if (pipe.Zone === activetab && pipe.ProjectID === jobDetails.ProjectID) {
              updatedFormData[pipe.ID] = pipe;
            }
          });
        }
        return updatedFormData;
      });
    } catch (error) {
      console.error("Failed to add drop pipe:", error);
      setError(error.message || "An error occurred while adding the drop pipe.");
    }
  };

  const handleDeleteDropPipe = async (pipeId) => {
    if (!pipeId) {
      setError("Invalid pipe ID.");
      return;
    }

    try {
      // Find the drop pipe to delete for change log
      const pipeToDelete = Object.values(formData).find(pipe => pipe.ID === pipeId);
      
      // Delete the specified drop pipe and refetch job details
      await dispatch(deleteData({ url: apiUrl, deleteId: { ID: pipeId } })).unwrap();

      // Post to change log after successful deletion
      if (pipeToDelete) {
        // Create a descriptive string for the change log
        const pipeDescription = `Drop Pipe ${pipeToDelete.DropPipeID || "Unknown"}`;
        
        await dispatch(postChangeLog({
          projectID: jobId,
          currentUser: currentUser,
          userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
          section: "Drop Pipe Specs",
          field: pipeDescription,
          prevValue: "",
          newValue: "DELETED",
          dateTime: new Date().toISOString()
        })).unwrap();
      }

      dispatch(clearSectionJobDetails(sectionName));

      const updatedDetails = await dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })).unwrap();


      setFormData((prevData) => {
        const updatedFormData = { ...prevData };
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((pipe) => {
            if (pipe.Zone === activetab && pipe.ProjectID === jobDetails.ProjectID) {
              updatedFormData[pipe.ID] = pipe;
            }
          });
        }
        return updatedFormData;
      });
    } catch (error) {
      console.error("Failed to delete drop pipe:", error);
      setError(error.message || "An error occurred while deleting the drop pipe.");
    }
  };
  

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="drop-pipe-specs">
      <SaveBanner showNotification={showNotification} saveStatus={saveStatus} />
      <Row className="mb-3 drop-pipe-specs-header">
        <Col sm={1} className="drop-pipe-id-field">
          <OverlayTrigger
            placement="top"
            overlay={renderTooltip("DropPipeID")}
            delay={{ show: 300, hide: 200 }}
          >
            <Form.Label>ID</Form.Label>
          </OverlayTrigger>
        </Col>
        <Col className="drop-pipe-inlet-field">
          <OverlayTrigger
            placement="top"
            overlay={renderTooltip("InletSize")}
            delay={{ show: 300, hide: 200 }}
          >
            <Form.Label>Inlet Size</Form.Label>
          </OverlayTrigger>
        </Col>
        <Col className="drop-pipe-discharge-field">
          <OverlayTrigger
            placement="top"
            overlay={renderTooltip("DischargeSize")}
            delay={{ show: 300, hide: 200 }}
          >
            <Form.Label>Discharge Size</Form.Label>
          </OverlayTrigger>
        </Col>
        <Col className="drop-pipe-paint-field">
          <OverlayTrigger
            placement="top"
            overlay={renderTooltip("PaintColor")}
            delay={{ show: 300, hide: 200 }}
          >
            <Form.Label>Paint Color</Form.Label>
          </OverlayTrigger>
        </Col>
        <Col className="drop-pipe-details-field">
          <OverlayTrigger
            placement="top"
            overlay={renderTooltip("Details")}
            delay={{ show: 300, hide: 200 }}
          >
            <Form.Label>Details</Form.Label>
          </OverlayTrigger>
        </Col>
        <Col sm={1}></Col>
      </Row>
      {Object.keys(formData).map((pipeId) => {
        const pipe = formData[pipeId];

        return (
          <Row key={pipe.ID} className="mb-3 drop-pipe-specs-record">
            {/* Drop Pipe ID */}
            <Col sm={1} className="drop-pipe-field drop-pipe-id-field">
              <Form.Group controlId={`dropPipeID-${pipe.ID}`}>
                <Form.Control
                  type="text"
                  name="DropPipeID"
                  value={pipe.DropPipeID || ""}
                  onFocus={handleFocus}
                  onBlur={(e) =>
                    handleBlur(e, `Drop Pipe ${pipe.DropPipeID} ID `, [], {
                      ID: pipe.ID,
                      url: apiUrl,
                      field: "DropPipeID",
                      tableKey,
                    })
                  }
                  className={getFieldHighlightClass("DropPipeID", pipe.ID)} // Apply dynamic class
                  onChange={(e) => handleChange(e, pipe.ID)}
                  placeholder="Enter Drop Pipe ID"
                />
              </Form.Group>
            </Col>

            {/* Inlet Size */}
            <Col className="drop-pipe-field drop-pipe-inlet-field">
              <Form.Group controlId={`inletSize-${pipe.ID}`}>
                <Form.Control
                  type="text"
                  name="InletSize"
                  value={pipe.InletSize || ""}
                  onFocus={handleFocus}
                  onBlur={async (e) => {
                    await handleBlur(e, `Drop Pipe ${pipe.DropPipeID} Inlet Size`, [], {
                      ID: pipe.ID,
                      url: apiUrl,
                      field: "InletSize",
                      tableKey,
                    });
                    setSaveStatus("Saved successfully");
                    setShowNotification(true);
                    setTimeout(() => setShowNotification(false), 3000);
                  }}
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={getFieldHighlightClass("InletSize", pipe.ID)} // Apply dynamic class
                  onChange={(e) => handleChange(e, pipe.ID)}
                  placeholder="Enter Inlet Size"
                />
              </Form.Group>
            </Col>

            {/* Discharge Size */}
            <Col className="drop-pipe-field drop-pipe-discharge-field">
              <Form.Group controlId={`dischargeSize-${pipe.ID}`}>
                <Form.Control
                  type="text"
                  name="DischargeSize"
                  value={pipe.DischargeSize || ""}
                  onFocus={handleFocus}
                  onBlur={async (e) => {
                    await handleBlur(e, `Drop Pipe ${pipe.DropPipeID} Discharge Size`, [], {
                      ID: pipe.ID,
                      url: apiUrl,
                      field: "DischargeSize",
                      tableKey,
                    });
                    setSaveStatus("Saved successfully");
                    setShowNotification(true);
                    setTimeout(() => setShowNotification(false), 3000);
                  }}
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={getFieldHighlightClass("DischargeSize", pipe.ID)} // Apply dynamic class
                  onChange={(e) => handleChange(e, pipe.ID)}
                  placeholder="Enter Discharge Size"
                />
              </Form.Group>
            </Col>

            {/* Paint Color */}
            <Col className="drop-pipe-field drop-pipe-paint-field">
              <Form.Group controlId={`paintColor-${pipe.ID}`}>
                <Form.Select
                  name="PaintColor"
                  value={pipe.PaintColor || ""}
                  onFocus={handleFocus}
                  onBlur={async (e) => {
                    await handleBlur(e, `Drop Pipe ${pipe.DropPipeID} Paint Color`, [], {
                      ID: pipe.ID,
                      url: apiUrl,
                      field: "PaintColor",
                      tableKey,
                    });
                    setSaveStatus("Saved successfully");
                    setShowNotification(true);
                    setTimeout(() => setShowNotification(false), 3000);
                  }}
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={getFieldHighlightClass("PaintColor", pipe.ID)} // Apply dynamic class
                  onChange={(e) => handleChange(e, pipe.ID)}
                >
                  <option value="">Select Paint Color</option>
                  {paintColorOptions?.map((option) => (
                    <option key={option.ID} value={option.DisplayText}>
                      {option.DisplayText === "Same as Station"
                        ? `Same as Station (${stationColor})`
                        : option.DisplayText}
                    </option>
                  ))}
                  {!isValueInDropdown(pipe.PaintColor, paintColorOptions) &&
                    pipe.PaintColor && (
                      <option
                        value={pipe.PaintColor}
                        style={{ color: "red" }}
                      >
                        *{pipe.PaintColor}
                      </option>
                    )}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Details */}
            <Col className="drop-pipe-field drop-pipe-details-field">
              <Form.Group controlId={`details-${pipe.ID}`}>
                <Form.Control
                  type="text"
                  name="Details"
                  value={pipe.Details || ""}
                  onFocus={handleFocus}
                  onBlur={async (e) => {
                    await handleBlur(e, `Drop Pipe ${pipe.DropPipeID} Details`, [], {
                      ID: pipe.ID,
                      url: apiUrl,
                      field: "Details",
                      tableKey,
                    });
                    setSaveStatus("Saved successfully");
                    setShowNotification(true);
                    setTimeout(() => setShowNotification(false), 3000);
                  }}
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                  className={getFieldHighlightClass("Details", pipe.ID)} // Apply dynamic class
                  onChange={(e) => handleChange(e, pipe.ID)}
                  placeholder="Enter Details"
                />
              </Form.Group>
            </Col>

            {/* Delete Button */}
            <Col sm={1} className="drop-pipe-actions">
              <Button
                variant="link"
                onClick={() => handleDeleteDropPipe(pipe.ID)}
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
              >
                <i className="bi bi-trash"></i>
              </Button>
            </Col>
          </Row>
        );
      })}
      <Button
        className="responsive-add-row-button"
        variant="primary"
        onClick={handleAddDropPipe}
        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
      >
        Add Drop Pipe
      </Button>
    </div>
  );
};

export default DropPipeSpecs;

